import styles from './EmplyeesListStyles';
import {
  Body1,
  Button,
  Select,
  Tooltip,
  useFluent,
  useScrollbarWidth
} from '@fluentui/react-components';
import {
  Card,
  createTableColumn,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableRowData as RowStateBase,
  useTableFeatures,
  useTableSelection
} from '@fluentui/react-components/unstable';
import { FixedSizeList as List } from "react-window";
import React, { useContext, useEffect, useRef, useState } from 'react';
import UserContext from '../../modules/AppRouter/UserContext';
import { AppButton, AppErrorToast, AppLoader, appSuccessToast } from '../common';
import { userRoleEnum } from '../../utils/enums';
import {
  IExpectedHoursItem,
  IExpectedHoursListFilters,
  IExpectedHoursTabProps,
} from '../../services/types/expectedHours';
import ExpectedHoursRow from '../businessComponenets/ExpectedHoursRow';
import { importExcelAndSend, fetchLastUpdated } from '../../services/excelImportService';
import { getSettlementSelectableYears, monthNames } from '../../utils/consts';
import usersService from '../../services/users.service';
import type { IUserSelectable } from '../../services/types/users';

interface TableRowData extends RowStateBase<IExpectedHoursItem> {
  selected: boolean;
  appearance: "brand" | "none";
}

const VIRTUAL_ROW_HEIGHT = 48;

function renderHeaderWithTooltip(label: string, content: string | JSX.Element) {
  return (
    <span style={styles.headerTooltipLabel}>
      {label}
      <Tooltip relationship="description" content={content}>
        <span style={styles.headerTooltipIcon} aria-label={`Informacja: ${label}`}>i</span>
      </Tooltip>
    </span>
  );
}

const absentifyTooltipContent = (
  <div>
    Godziny nieobecności, które pomniejszają rozliczenie godzin usług.
    <br />
    Wliczane typy:
    <br />- wypoczynkowy urlop
    <br />- zwolnienie lekarskie
    <br />- niepełny etat
    <br />- macierzyński urlop
    <br />- ojcowski urlop
    <br />- okolicznościowy urlop
    <br />- bezpłatny urlop
    <br />- dzień wolny za święto
    <br />- przerwa w świadczeniu usług (B2B,UZ)
  </div>
);

const totalTooltipContent = (
  <div>
    Rzeczywiście rozliczone godziny usług w miesiącu.
    <br />
    Liczenie:
    <br />
    YouTrack + narzut za 150% i 200% (dodatkowo: 150% = +0,5h za 1h, 200% = +1h za 1h) + odebrane nadgodziny/wyjścia.
  </div>
);

const targetTooltipContent = (
  <div>
    Docelowa liczba godzin możliwych do rozliczenia.
    <br />
    Bazowo:
    <br />
    Godziny robocze - Absentify + nadgodziny 100% + (nadgodziny 150% x 1,5) + (nadgodziny 200% x 2).
    <br />
    Dodatkowo, gdy Nadgodziny do odebrania &lt; 0, wynik jest pomniejszany o brakujące godziny.
  </div>
);

const columns = [
  createTableColumn<IExpectedHoursItem>({ columnId: "userName" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "contractType" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "youtrack" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "absentify" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "toCollect" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "collected" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout100" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout150" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout200" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "total" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "workingHours" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "target" }),
];

const ExpectedHoursList: React.FC<IExpectedHoursTabProps> = ({
  tableHeadTxt,
  items,
  isLoading,
  getMoreItems,
  selectedMonth,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
  acceptSettlement,
  rejectSettlement,
  filters,
  setFilters
}) => {
  const { targetDocument } = useFluent();
  const scrollbarWidth = useScrollbarWidth({ targetDocument });

  const {
    getRows,
    selection: { toggleRow, isRowSelected },
  } = useTableFeatures(
    {
      columns,
      items,
    },
    [useTableSelection({ selectionMode: "multiselect" })]
  );

  const { userData } = useContext(UserContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('Aktualizacja Absentify: brak');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [selectableUsers, setSelectableUsers] = useState<IUserSelectable[]>([]);

  useEffect(() => {
    usersService.selectableUsers()
      .then(setSelectableUsers)
      .catch(() => setSelectableUsers([]));
  }, []);

  const rows: TableRowData[] = getRows((row) => {
    const selected = isRowSelected(row.rowId);
    return {
      ...row,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === " ") {
          e.preventDefault();
          toggleRow(e, row.rowId);
        }
      },
      selected,
      appearance: selected ? "brand" : "none",
    };
  });

  const goBack = () => window.history.back();

  const onMonthSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(e.target.value);
  };

  const onYearSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const onEmployeeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, employeeId: value || undefined }));
  };

  const onContractTypeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as IExpectedHoursListFilters["contractType"] | "";
    setFilters((prev) => ({ ...prev, contractType: value || undefined }));
  };

  const onSettlementStatusSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as IExpectedHoursListFilters["settlementStatus"] | "";
    setFilters((prev) => ({ ...prev, settlementStatus: value || undefined }));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      await importExcelAndSend(file, selectedYear);

      appSuccessToast("✅ Dane zostały zaimportowane pomyślnie.");

      const updated = await fetchLastUpdated(selectedYear);
      setLastUpdated(`Aktualizacja Absentify: ${updated}`);

      getMoreItems();
    } catch (err: any) {
      AppErrorToast(err.message || "❌ Wystąpił błąd podczas importu pliku.");
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    fetchLastUpdated(selectedYear)
      .then((updated) => setLastUpdated(`Aktualizacja Absentify: ${updated}`))
      .catch(() => setLastUpdated('Aktualizacja Absentify: brak danych'));
  }, [selectedMonth, selectedYear]);

  return (
    <Card style={styles.tableContainer}>
      <Body1>
        <Button appearance="primary" onClick={goBack} style={styles.buttonBack}>
          Powrót
        </Button>

        <div style={styles.tableWithFiltersHeader}>
          {tableHeadTxt ? <h3 style={styles.tableHeadTxt}>{tableHeadTxt}</h3> : null}
          <div style={styles.expectedHoursToolbarRow}>
            Typ umowy:
            <Select style={styles.tableFilter} onChange={onContractTypeSelect} value={filters.contractType ?? ""}>
              <option value="">Wszystkie</option>
              <option value="NONE">Brak</option>
              <option value="B2B">Umowa B2B</option>
              <option value="EmploymentContract">Umowa o pracę</option>
              <option value="ContractOfMandate">Umowa zlecenie</option>
            </Select>

            Pracownik:
            <Select style={styles.tableFilter} onChange={onEmployeeSelect} value={filters.employeeId ?? ""}>
              <option value="">Wszyscy</option>
              {selectableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.lastName} {u.firstName}
                </option>
              ))}
            </Select>

            Status:
            <Select style={styles.tableFilter} onChange={onSettlementStatusSelect} value={filters.settlementStatus ?? ""}>
              <option value="">Wszystkie</option>
              <option value="NONE">Brak decyzji</option>
              <option value="Accepted">Zaakceptowany</option>
              <option value="Rejected">Odrzucony</option>
            </Select>

            Miesiąc:
            <Select style={styles.tableFilter} onChange={onMonthSelect} value={selectedMonth}>
              {monthNames.map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </Select>
            Rok:
            <Select style={styles.tableFilter} onChange={onYearSelect} value={selectedYear}>
              {getSettlementSelectableYears().map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Select>

            <span style={{ flexShrink: 0 }}>
              <AppButton
                text="Zaktualizuj dane z Absentify"
                disabled={isUploading}
                appearance="secondary"
                onClick={handleUploadClick}
              />
            </span>
            <input
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              style={styles.hiddenFileInput}
              onChange={handleFileChange}
            />
            <Tooltip relationship="description" content={lastUpdated}>
              <span style={styles.headerTooltipIcon} aria-label={lastUpdated}>
                i
              </span>
            </Tooltip>
          </div>
        </div>

        <div className="main-table-container">
          <Table
            noNativeElements
            aria-label="Tabela rozliczeń godzin usług"
            aria-rowcount={rows.length}
            style={styles.table}
          >
            <TableHeader>
              <TableRow aria-rowindex={1}>
                <TableHeaderCell>Nazwisko i imię</TableHeaderCell>
                <TableHeaderCell>Typ umowy</TableHeaderCell>
                <TableHeaderCell>YouTrack</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Absentify", absentifyTooltipContent)}</TableHeaderCell>
                <TableHeaderCell>Nadgodziny do odebrania</TableHeaderCell>
                <TableHeaderCell>Odebrane nadgodziny/wyjścia</TableHeaderCell>
                <TableHeaderCell>Nadgodziny 100%</TableHeaderCell>
                <TableHeaderCell>Nadgodziny 150%</TableHeaderCell>
                <TableHeaderCell>Nadgodziny 200%</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Suma", totalTooltipContent)}</TableHeaderCell>
                <TableHeaderCell>Godziny robocze</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Docelowo", targetTooltipContent)}</TableHeaderCell>
                <TableHeaderCell style={styles.actionsHeaderCell}>Akcje</TableHeaderCell>
                <div role="presentation" style={{ width: scrollbarWidth }} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length <= 0 && !isLoading && (
                <h4 className="table-empty-value">Brak danych</h4>
              )}

              <List
                height={350}
                itemCount={rows.length}
                itemSize={VIRTUAL_ROW_HEIGHT}
                width="100%"
                itemData={rows}
              >
                {({ index, style, data }) => (
                  <ExpectedHoursRow
                    index={index}
                    style={style}
                    data={data}
                    acceptSettlement={acceptSettlement}
                    rejectSettlement={rejectSettlement}
                    canManageActions={userData.data.baseRole === userRoleEnum.Admin}
                  />
                )}
              </List>

              {isLoading && items.length === 0 && <AppLoader />}
            </TableBody>
          </Table>
        </div>
      </Body1>
    </Card>
  );
};

export default ExpectedHoursList;
